// LabelPopup.js
import React, { useState, useEffect, useRef } from 'react';
import Draggable from 'react-draggable';
import './css/popup.css';
import { Translator, resources } from './Translator';

const translator = new Translator(resources);

const LABEL_COLORS = [
    '#FF0000', '#00FF00', '#0000FF', '#FFFF00',
    '#FF00FF', '#00FFFF', '#800000', '#008000',
    '#000080', '#808000', '#800080', '#008080',
    '#C0C0C0', '#808080', '#FFFFFF', '#000000',
];

const normalizeColor = (c) => {
    if (!c) return '#FF0000';
    const hex = c.slice(0, 7).toUpperCase();
    return LABEL_COLORS.includes(hex) ? hex : '#FF0000';
};

// 背景色に対して読みやすい文字色を返す
const textColorFor = (bg) => {
    const r = parseInt(bg.slice(1, 3), 16);
    const g = parseInt(bg.slice(3, 5), 16);
    const b = parseInt(bg.slice(5, 7), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 >= 128 ? '#000' : '#fff';
};

const ColorDropdown = ({ value, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handleClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    return (
        <div ref={ref} style={{ position: 'relative', display: 'inline-block', userSelect: 'none' }}>
            {/* トリガー */}
            <div
                onClick={() => setOpen(o => !o)}
                style={{
                    width: '120px', height: '24px', background: value,
                    border: '1px solid #666', borderRadius: '3px',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                    paddingRight: '6px', boxSizing: 'border-box',
                    color: textColorFor(value), fontSize: '12px',
                }}
            >
                ▾
            </div>

            {/* ドロップダウンリスト */}
            {open && (
                <div style={{
                    position: 'absolute', top: '26px', left: 0, zIndex: 9999,
                    border: '1px solid #666', borderRadius: '3px',
                    background: '#222', boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                    minWidth: '120px',
                }}>
                    {LABEL_COLORS.map((c) => {
                        const selected = c === value;
                        const fg = textColorFor(c);
                        return (
                            <div
                                key={c}
                                onMouseDown={(e) => { e.preventDefault(); onChange(c); setOpen(false); }}
                                style={{
                                    background: c, height: '24px',
                                    display: 'flex', alignItems: 'center',
                                    paddingLeft: '6px', cursor: 'pointer',
                                    color: fg, fontSize: '14px', fontWeight: 'bold',
                                }}
                            >
                                {selected ? '✓' : ''}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const LabelPopup = ({
    mode, // 'add' | 'edit'
    initialText,
    initialFontSize,
    initialColor,
    onClose,
    onAdd,
    onSave,
}) => {
    const t = (key) => translator.t(key);

    const [text, setText] = useState(initialText || '');
    const [fontSize, setFontSize] = useState(
        typeof initialFontSize === 'number' ? initialFontSize : (parseInt(initialFontSize, 10) || 20)
    );
    const [color, setColor] = useState(() => normalizeColor(initialColor));

    const popupRef = useRef(null);

    useEffect(() => {
        if (popupRef.current) {
            const { width, height } = popupRef.current.getBoundingClientRect();
            popupRef.current.style.setProperty('--popup-width', `${width}px`);
            popupRef.current.style.setProperty('--popup-height', `${height}px`);
        }
    }, []);

    const handlePrimary = () => {
        // 複数行対応: 改行コードを \n に揃え、各行末の空白と前後の空行を除く
        const trimmed = (text || '')
            .replace(/\r\n?/g, '\n')
            .split('\n').map(line => line.replace(/\s+$/, '')).join('\n')
            .replace(/^\n+|\n+$/g, '');
        if (!trimmed) return;
        const size = parseInt(fontSize, 10) || 20;
        if (mode === 'add') {
            if (onAdd) onAdd(trimmed, size, color);
        } else {
            if (onSave) onSave(trimmed, size, color);
        }
    };

    // Enter は改行。Shift+Enter / Ctrl+Enter / Cmd+Enter で確定する
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && (e.shiftKey || e.ctrlKey || e.metaKey) && !e.nativeEvent.isComposing) {
            e.preventDefault();
            handlePrimary();
        }
    };

    const primaryLabel = mode === 'add' ? t('Add') : t('Save');
    const title = mode === 'add' ? t('Add Label') : t('Edit Label');
    const isTextEmpty = !(text || '').trim();

    const rowStyle = { display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '8px' };
    const labelStyle = { flex: '0 0 80px', fontSize: '0.9em', textAlign: 'left' };

    return (
        <Draggable handle=".popup-header">
            <div className="popup palette_popup" ref={popupRef}>
                <div className="popup-inner">
                    <div className="popup-header">
                        <h2>{title}</h2>
                        <span className="close_button" onClick={onClose}>&times;</span>
                    </div>

                    <div style={{ ...rowStyle, marginTop: '8px', alignItems: 'flex-start' }}>
                        <span style={{ ...labelStyle, paddingTop: '4px' }}>{t('Text')}</span>
                        <textarea
                            value={text}
                            autoFocus
                            rows={3}
                            title={t('LabelMultilineHint')}
                            placeholder={t('LabelMultilineHint')}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            style={{ flex: 1, resize: 'vertical', minHeight: '3.6em', fontFamily: 'inherit', fontSize: 'inherit', boxSizing: 'border-box' }}
                        />
                    </div>

                    <div style={rowStyle}>
                        <span style={labelStyle}>{t('Size')}</span>
                        <input
                            type="number" min={8} max={200}
                            value={fontSize}
                            onChange={(e) => setFontSize(e.target.value)}
                            style={{ width: '80px' }}
                        />
                    </div>

                    <div style={rowStyle}>
                        <span style={labelStyle}>{t('Color')}</span>
                        <ColorDropdown value={color} onChange={setColor} />
                    </div>

                    <button className="button_b" onClick={onClose}>{t('Close')}</button>
                    <button className="button_y" onClick={handlePrimary} disabled={isTextEmpty}
                        style={{ opacity: isTextEmpty ? 0.35 : 1, transition: 'opacity 0.15s' }}>{primaryLabel}</button>
                </div>
            </div>
        </Draggable>
    );
};

export default LabelPopup;
